import {
  isCustomerHistoryAnchorCandidate,
  isCustomerHistoryCoverageMessage,
  isCustomerVisibleMessage
} from "./message-visibility";

const DEFAULT_RETRY_DELAYS = [0, 500, 1500];
// A long backlog is split into resumable chunks. This bounds one request chain
// without restarting from the newest page and starving an older gap.
const DEFAULT_MAX_PAGES = 100;
const DEFAULT_MAX_CHUNK_DURATION_MS = 15 * 1000;
const DEFAULT_MAX_TRACKED_CURSORS = 501;
const DEFAULT_OVERLAP_PAGES = 1;
const DEFAULT_HEAD_PROBE_WINDOW_MESSAGES = 500;

export const HISTORY_COVERAGE_MODE = Object.freeze({
  FINITE: "finite",
  // No trustworthy local anchor yet. Scan to the terminal cursor, then require
  // a delayed terminal verification before adopting a bounded recent floor.
  PROVISIONAL: "provisional",
  // An unpositioned server/history record makes a bounded floor unsafe.
  TERMINAL: "terminal"
});

const wait = delay =>
  new Promise(resolve => {
    setTimeout(resolve, delay);
  });

const hasValue = value => value !== undefined && value !== null && value !== "";

const toFiniteNumber = value => {
  if (!hasValue(value)) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const toFiniteCursor = value => {
  if (typeof value === "number") {
    return Number.isSafeInteger(value) ? value : undefined;
  }
  if (typeof value !== "string") return undefined;

  const normalized = value.trim();
  if (!/^-?\d+$/.test(normalized)) return undefined;
  const number = Number(normalized);
  return Number.isSafeInteger(number) ? number : undefined;
};

const toStableSort = value => {
  const sort = toFiniteNumber(value);
  return sort !== undefined && sort > 0 ? sort : undefined;
};

const cursorKey = cursor => String(toFiniteCursor(cursor));

const getMessageIdentity = message => {
  if (!message || typeof message !== "object") return {};

  let msgIdKey;
  let sortKey;
  if (hasValue(message.msgId)) {
    msgIdKey = `msg:${JSON.stringify(String(message.msgId))}`;
  }

  const sort = toStableSort(message.sort);
  if (hasValue(message.channelId) && sort !== undefined) {
    sortKey = `sort:${JSON.stringify([
      String(message.channelId),
      String(sort)
    ])}`;
  }

  return { msgIdKey, sortKey };
};

const getStableMessageKeys = message => {
  const identity = getMessageIdentity(message);
  return [identity.msgIdKey, identity.sortKey].filter(Boolean);
};

const hasStableCoordinateConflict = (left, right) => {
  const leftSort = toStableSort(left && left.sort);
  const rightSort = toStableSort(right && right.sort);
  if (
    leftSort === undefined ||
    rightSort === undefined ||
    !hasValue(left && left.channelId) ||
    !hasValue(right && right.channelId)
  ) {
    return false;
  }

  return (
    String(left.channelId) !== String(right.channelId) || leftSort !== rightSort
  );
};

const parseJsonField = (message, field) => {
  const value = message[field];
  if (typeof value !== "string" || value === "") return;

  try {
    message[field] = JSON.parse(value);
  } catch (error) {
    // Keep malformed history data visible instead of failing the whole sync.
  }
};

export const normalizeHistoryMessage = message => {
  if (!message || typeof message !== "object") return message;

  const normalized = { ...message };
  parseJsonField(normalized, "payDetail");
  parseJsonField(normalized, "orderDetail");
  return normalized;
};

export const createHistoryAnchorKeys = messages => {
  const anchors = new Set();

  (Array.isArray(messages) ? messages : []).forEach(message => {
    getStableMessageKeys(message).forEach(key => anchors.add(key));
  });

  return anchors;
};

const getMessageTime = message => {
  if (!message || typeof message !== "object") return undefined;
  const value = hasValue(message.chatTime)
    ? message.chatTime
    : message.createTime;
  return toFiniteNumber(value);
};

const shouldFillMessageField = (existing, incoming, key) => {
  const missingValue = !hasValue(existing && existing[key]);
  const missingSort =
    key === "sort" &&
    toStableSort(existing && existing[key]) === undefined &&
    toStableSort(incoming && incoming[key]) !== undefined;
  return (missingValue && hasValue(incoming && incoming[key])) || missingSort;
};

const createStableAnchorMatcher = anchorMessages => {
  const msgIdMap = new Map();
  const sortMap = new Map();

  (Array.isArray(anchorMessages) ? anchorMessages : []).forEach(anchor => {
    const identity = getMessageIdentity(anchor);
    if (identity.msgIdKey) {
      const matches = msgIdMap.get(identity.msgIdKey) || [];
      matches.push(anchor);
      msgIdMap.set(identity.msgIdKey, matches);
    }
    if (identity.sortKey) {
      const matches = sortMap.get(identity.sortKey) || [];
      matches.push(anchor);
      sortMap.set(identity.sortKey, matches);
    }
  });

  const find = message => {
    const identity = getMessageIdentity(message);
    if (identity.msgIdKey) {
      const msgIdMatches = msgIdMap.get(identity.msgIdKey) || [];
      if (
        msgIdMatches.length === 1 &&
        !hasStableCoordinateConflict(msgIdMatches[0], message)
      ) {
        return msgIdMatches[0];
      }
      if (msgIdMatches.length > 0) return undefined;
    }

    if (identity.sortKey) {
      const sortMatches = (sortMap.get(identity.sortKey) || []).filter(
        anchor => {
          const anchorIdentity = getMessageIdentity(anchor);
          return !identity.msgIdKey || !anchorIdentity.msgIdKey;
        }
      );
      if (sortMatches.length === 1) return sortMatches[0];
    }

    return undefined;
  };

  return { find, matches: message => find(message) !== undefined };
};

const createHistoryBoundary = (messages, preferredChannelId) => {
  let channelId;
  let minSort;
  let maxSort;

  const sourceMessages = (Array.isArray(messages) ? messages : []).filter(
    message => message && typeof message === "object"
  );
  channelId = hasValue(preferredChannelId)
    ? preferredChannelId
    : (sourceMessages.find(message => hasValue(message.channelId)) || {})
        .channelId;
  const boundaryMessages = sourceMessages.filter(
    message =>
      !hasValue(channelId) ||
      !hasValue(message.channelId) ||
      String(message.channelId) === String(channelId)
  );
  const hasCompleteSortBoundary =
    boundaryMessages.length > 0 &&
    boundaryMessages.every(message => toStableSort(message.sort) !== undefined);

  boundaryMessages.forEach(message => {
    const sort = toStableSort(message.sort);
    if (hasCompleteSortBoundary && (minSort === undefined || sort < minSort)) {
      minSort = sort;
    }
    if (hasCompleteSortBoundary && (maxSort === undefined || sort > maxSort)) {
      maxSort = sort;
    }
  });

  return { channelId, minSort, maxSort };
};

const selectOldestAnchorMessages = (messages, boundary) => {
  const candidates = (Array.isArray(messages) ? messages : []).filter(message =>
    isBoundaryChannel(message, boundary)
  );

  if (boundary.minSort !== undefined) {
    return candidates.filter(
      message => toStableSort(message && message.sort) === boundary.minSort
    );
  }

  // Time is not a reliable coverage coordinate. Without a complete positive
  // sort snapshot there is no safe local floor, so the collector must scan to
  // the API terminal cursor.
  return [];
};

const isBoundaryChannel = (message, boundary) =>
  !hasValue(boundary.channelId) ||
  !hasValue(message && message.channelId) ||
  String(message.channelId) === String(boundary.channelId);

const selectMessagesWithinBoundary = (
  messages,
  anchorMessages,
  oldestAnchorMessages,
  boundary
) => {
  const anchorMatcher = createStableAnchorMatcher(anchorMessages);
  const oldestAnchorMatcher = createStableAnchorMatcher(oldestAnchorMessages);
  const boundaryMessages = messages.filter(message =>
    isBoundaryChannel(message, boundary)
  );
  const pageSorts = boundaryMessages
    .map(message => toStableSort(message && message.sort))
    .filter(sort => sort !== undefined);
  const reachedOldestAnchor = messages.some(
    message =>
      isBoundaryChannel(message, boundary) &&
      oldestAnchorMatcher.matches(message)
  );
  const reachedSortBoundary =
    boundary.minSort !== undefined &&
    pageSorts.length > 0 &&
    pageSorts.length === boundaryMessages.length &&
    pageSorts.every(sort => sort <= boundary.minSort);
  const reachedAnchor = reachedOldestAnchor || reachedSortBoundary;
  return {
    messages: messages.filter(message => {
      if (!isBoundaryChannel(message, boundary)) return false;

      const existingAnchor = anchorMatcher.find(message);
      if (
        existingAnchor &&
        !Object.keys(message || {}).some(key =>
          shouldFillMessageField(existingAnchor, message, key)
        )
      ) {
        return false;
      }

      // The floor controls when the bounded scan stops; it never filters an
      // already fetched candidate. This also covers unstable page ordering.
      // Stable identities are deduplicated later. Keyless records are retained
      // to favour completeness and surfaced by merge diagnostics.
      return true;
    }),
    reachedAnchor
  };
};

const isTerminalCursor = cursor => {
  const numericCursor = toFiniteCursor(cursor);
  return numericCursor !== undefined && numericCursor < 0;
};

const isContinuationCursor = cursor => {
  const numericCursor = toFiniteCursor(cursor);
  return numericCursor !== undefined && numericCursor >= 0;
};

const isZeroCursorTerminalCandidate = (messages, cursor) =>
  toFiniteCursor(cursor) === 0 &&
  (messages === null || (Array.isArray(messages) && messages.length === 0));

const isHistoryMessageRecord = message =>
  message && typeof message === "object" && !Array.isArray(message);

const parseHistoryResponse = response => {
  if (!response || response.code != 200 || !response.data) {
    throw new Error("Reconnect history request failed");
  }

  const rawMessages = response.data.chatCusServerDetailList;
  const nextCursor = response.data.beginTime;
  // The history API uses a negative cursor as its explicit end marker. Some
  // deployments instead reset an empty page to cursor zero; that ambiguous
  // shape must be confirmed by the request retry chain before it is terminal.
  const terminal = isTerminalCursor(nextCursor);
  const terminalCandidate = isZeroCursorTerminalCandidate(
    rawMessages,
    nextCursor
  );
  const supportedPayload =
    Array.isArray(rawMessages) ||
    (rawMessages === null && (terminal || terminalCandidate));
  if (!supportedPayload) {
    throw new Error("Reconnect history response is malformed");
  }

  const messages = Array.isArray(rawMessages)
    ? rawMessages.filter(isHistoryMessageRecord)
    : [];
  if (
    Array.isArray(rawMessages) &&
    rawMessages.length > 0 &&
    !messages.length
  ) {
    throw new Error("Reconnect history response is malformed");
  }

  return {
    messages,
    malformedCount: Array.isArray(rawMessages)
      ? rawMessages.filter(message => !isHistoryMessageRecord(message)).length
      : 0,
    nextCursor,
    terminal,
    terminalCandidate
  };
};

const createExactMessageFingerprint = message => {
  const stack = new WeakSet();
  const normalize = value => {
    if (!value || typeof value !== "object") return value;
    if (stack.has(value)) throw new TypeError("Circular message data");
    stack.add(value);

    if (Array.isArray(value)) {
      const result = value.map(normalize);
      stack.delete(value);
      return result;
    }

    const result = Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = normalize(value[key]);
        return result;
      }, {});
    stack.delete(value);
    return result;
  };

  try {
    return JSON.stringify(normalize(message));
  } catch (error) {
    return undefined;
  }
};

const requestHistoryPage = async ({
  fetchPage,
  cursor,
  retryDelays,
  waitForRetry,
  shouldContinue
}) => {
  let lastError;
  let terminalCandidateSeen = false;

  for (let index = 0; index < retryDelays.length; index += 1) {
    if (!shouldContinue()) return { cancelled: true };

    const delay = retryDelays[index];
    if (delay > 0) await waitForRetry(delay);
    if (!shouldContinue()) return { cancelled: true };

    try {
      const page = parseHistoryResponse(await fetchPage(cursor));
      if (page.terminalCandidate) {
        if (terminalCandidateSeen) {
          return {
            ...page,
            terminal: true,
            terminalCandidate: false
          };
        }
        terminalCandidateSeen = true;
        lastError = new Error(
          "Reconnect history zero cursor terminal requires confirmation"
        );
        continue;
      }
      return page;
    } catch (error) {
      terminalCandidateSeen = false;
      lastError = error;
    }
  }

  throw lastError || new Error("Reconnect history request failed");
};

/**
 * Fetch newest history pages until reaching the fixed pre-reconnect boundary.
 * This function never reads or mutates the normal history-pagination cursor.
 */
export const collectReconnectHistory = async ({
  fetchPage,
  existingMessages = [],
  coverageFloorMessages,
  channelId,
  retryDelays = DEFAULT_RETRY_DELAYS,
  waitForRetry = wait,
  shouldContinue = () => true,
  maxPages = DEFAULT_MAX_PAGES,
  maxDurationMs = DEFAULT_MAX_CHUNK_DURATION_MS,
  now = () => Date.now(),
  overlapPages = DEFAULT_OVERLAP_PAGES,
  continuation
}) => {
  if (typeof fetchPage !== "function") {
    throw new TypeError("fetchPage must be a function");
  }

  const delays =
    Array.isArray(retryDelays) && retryDelays.length > 0 ? retryDelays : [0];
  const fixedCoverageFloor = Array.isArray(coverageFloorMessages)
    ? coverageFloorMessages
    : existingMessages;
  const boundary = createHistoryBoundary(fixedCoverageFloor, channelId);
  const oldestAnchorMessages = selectOldestAnchorMessages(
    fixedCoverageFloor,
    boundary
  );
  const messages = [];
  const startedAt = now();
  let cursor =
    continuation && isContinuationCursor(continuation.cursor)
      ? continuation.cursor
      : 0;
  let pageCount = 0;
  let hasMorePages = true;
  let cancelled = false;
  let malformedCount = 0;
  let overlapPagesRemaining =
    continuation && continuation.overlapPagesRemaining !== undefined
      ? continuation.overlapPagesRemaining
      : undefined;
  const pageBudget = Number.isFinite(Number(maxPages))
    ? Math.max(1, Math.floor(Number(maxPages)))
    : DEFAULT_MAX_PAGES;
  const durationBudget = Number.isFinite(Number(maxDurationMs))
    ? Math.max(0, Number(maxDurationMs))
    : DEFAULT_MAX_CHUNK_DURATION_MS;
  const seenCursorKeys = new Set(
    continuation && Array.isArray(continuation.seenCursorKeys)
      ? continuation.seenCursorKeys
      : [cursorKey(0)]
  );
  seenCursorKeys.add(cursorKey(cursor));

  while (hasMorePages) {
    if (!shouldContinue()) {
      cancelled = true;
      break;
    }

    const page = await requestHistoryPage({
      fetchPage,
      cursor,
      retryDelays: delays,
      waitForRetry,
      shouldContinue
    });

    if (page.cancelled || !shouldContinue()) {
      cancelled = true;
      break;
    }

    pageCount += 1;
    malformedCount += page.malformedCount || 0;
    const selectedPage = selectMessagesWithinBoundary(
      page.messages,
      existingMessages,
      oldestAnchorMessages,
      boundary
    );
    messages.push(...selectedPage.messages);

    if (page.terminal) {
      hasMorePages = false;
      continue;
    }

    if (selectedPage.reachedAnchor && overlapPagesRemaining === undefined) {
      overlapPagesRemaining = Math.max(0, Number(overlapPages) || 0);
    } else if (overlapPagesRemaining !== undefined) {
      overlapPagesRemaining -= 1;
    }

    if (overlapPagesRemaining !== undefined && overlapPagesRemaining <= 0) {
      hasMorePages = false;
      continue;
    }

    if (!isContinuationCursor(page.nextCursor)) {
      throw new Error("Reconnect history response cursor is malformed");
    }

    const numericCursor = toFiniteCursor(cursor);
    const numericNextCursor = toFiniteCursor(page.nextCursor);
    const nextCursorKey = cursorKey(page.nextCursor);
    const isNewestPage =
      numericCursor === 0 && pageCount === 1 && !continuation;
    const movesBackward = isNewestPage
      ? numericNextCursor > 0
      : numericNextCursor >= 0 && numericNextCursor < numericCursor;
    if (!movesBackward || seenCursorKeys.has(nextCursorKey)) {
      throw new Error("Reconnect history cursor did not reach boundary");
    }

    seenCursorKeys.add(nextCursorKey);
    cursor = page.nextCursor;

    if (
      pageCount >= pageBudget ||
      (durationBudget > 0 && now() - startedAt >= durationBudget)
    ) {
      return {
        messages,
        pageCount,
        cancelled: false,
        malformedCount,
        continuation: {
          cursor,
          // beginTime must also move strictly backward, so retaining the most
          // recent bounded set is sufficient for explicit cycle diagnostics.
          seenCursorKeys: Array.from(seenCursorKeys).slice(
            -DEFAULT_MAX_TRACKED_CURSORS
          ),
          overlapPagesRemaining
        }
      };
    }
  }

  return {
    messages,
    pageCount,
    cancelled,
    malformedCount
  };
};

const compareMessages = (left, right) => {
  const leftTime = getMessageTime(left.message);
  const rightTime = getMessageTime(right.message);
  const leftSort = toStableSort(left.message && left.message.sort);
  const rightSort = toStableSort(right.message && right.message.sort);

  if (leftTime !== undefined && rightTime === undefined) return -1;
  if (leftTime === undefined && rightTime !== undefined) return 1;
  if (leftTime !== undefined && leftTime !== rightTime) {
    return leftTime - rightTime;
  }

  if (leftSort !== undefined && rightSort === undefined) return -1;
  if (leftSort === undefined && rightSort !== undefined) return 1;
  if (leftSort !== undefined && leftSort !== rightSort) {
    return leftSort - rightSort;
  }

  return left.index - right.index;
};

const addIdentityIndex = (indexMap, key, index) => {
  if (!key) return;
  const indexes = indexMap.get(key) || new Set();
  indexes.add(index);
  indexMap.set(key, indexes);
};

/**
 * Merge history into the latest in-memory list. msgId is authoritative. The
 * channelId+sort fallback is used only when at least one side has no msgId.
 * Ambiguous or conflicting identities stay separate instead of risking loss.
 */
export const mergeReconnectMessagesWithStats = (
  existingMessages,
  historyMessages
) => {
  const merged = [];
  const isExisting = [];
  const msgIdToIndexes = new Map();
  const sortToIndexes = new Map();
  const fingerprintToExistingIndexes = new Map();
  const consumedExactIndexes = new Set();
  let collisionCount = 0;
  let dedupedCount = 0;
  let keylessCount = 0;

  const registerIdentity = (message, index) => {
    const identity = getMessageIdentity(message);
    addIdentityIndex(msgIdToIndexes, identity.msgIdKey, index);
    addIdentityIndex(sortToIndexes, identity.sortKey, index);
  };

  const registerExistingFingerprint = (message, index) => {
    const fingerprint = createExactMessageFingerprint(message);
    if (!fingerprint) return;
    const indexes = fingerprintToExistingIndexes.get(fingerprint) || [];
    indexes.push(index);
    fingerprintToExistingIndexes.set(fingerprint, indexes);
  };

  const findExactDuplicate = (message, allowedIndexes) => {
    const fingerprint = createExactMessageFingerprint(message);
    if (!fingerprint) return -1;

    const indexes = allowedIndexes
      ? allowedIndexes.filter(
          index => createExactMessageFingerprint(merged[index]) === fingerprint
        )
      : fingerprintToExistingIndexes.get(fingerprint);
    if (!indexes || indexes.length === 0) return -1;

    const allowed = allowedIndexes ? new Set(allowedIndexes) : undefined;
    const index = indexes.find(
      candidate =>
        !consumedExactIndexes.has(candidate) &&
        (!allowed || allowed.has(candidate))
    );
    if (index === undefined) return -1;
    consumedExactIndexes.add(index);
    return index;
  };

  const findDuplicate = message => {
    const identity = getMessageIdentity(message);

    if (identity.msgIdKey && msgIdToIndexes.has(identity.msgIdKey)) {
      const indexes = Array.from(msgIdToIndexes.get(identity.msgIdKey));
      const compatibleIndexes = indexes.filter(
        index => !hasStableCoordinateConflict(merged[index], message)
      );
      if (compatibleIndexes.length === 1) {
        return { index: compatibleIndexes[0], collision: false };
      }

      const exactIndex = findExactDuplicate(message, compatibleIndexes);
      if (exactIndex !== -1) return { index: exactIndex, collision: false };
      return { index: -1, collision: true };
    }

    if (identity.sortKey && sortToIndexes.has(identity.sortKey)) {
      const indexes = Array.from(sortToIndexes.get(identity.sortKey));
      const compatibleIndexes = indexes.filter(index => {
        const existingIdentity = getMessageIdentity(merged[index]);
        return !(
          identity.msgIdKey &&
          existingIdentity.msgIdKey &&
          identity.msgIdKey !== existingIdentity.msgIdKey
        );
      });

      if (compatibleIndexes.length === 1) {
        return { index: compatibleIndexes[0], collision: false };
      }

      const exactIndex = findExactDuplicate(message, compatibleIndexes);
      if (exactIndex !== -1) return { index: exactIndex, collision: false };
      return { index: -1, collision: indexes.length > 0 };
    }

    return { index: findExactDuplicate(message), collision: false };
  };

  const fillMissingFields = (existing, incoming) => {
    let result = existing;

    Object.keys(incoming || {}).forEach(key => {
      if (shouldFillMessageField(existing, incoming, key)) {
        if (result === existing) result = { ...existing };
        result[key] = incoming[key];
      }
    });

    return result;
  };

  (Array.isArray(existingMessages) ? existingMessages : []).forEach(message => {
    const identity = getMessageIdentity(message);
    if (!identity.msgIdKey && !identity.sortKey) keylessCount += 1;
    const index = merged.length;
    merged.push(message);
    isExisting.push(true);
    registerIdentity(message, index);
    if (!identity.msgIdKey && !identity.sortKey) {
      registerExistingFingerprint(message, index);
    }
  });

  (Array.isArray(historyMessages) ? historyMessages : []).forEach(
    rawMessage => {
      if (!rawMessage || typeof rawMessage !== "object") {
        return;
      }
      const message = normalizeHistoryMessage(rawMessage);
      const identity = getMessageIdentity(message);
      if (!identity.msgIdKey && !identity.sortKey) keylessCount += 1;
      const duplicate = findDuplicate(message);
      const duplicateIndex = duplicate.index;
      if (duplicate.collision) collisionCount += 1;

      if (duplicateIndex === -1) {
        const index = merged.length;
        merged.push(message);
        isExisting.push(false);
        registerIdentity(message, index);
        return;
      }

      dedupedCount += 1;
      if (isExisting[duplicateIndex]) {
        merged[duplicateIndex] = fillMissingFields(
          merged[duplicateIndex],
          message
        );
      } else {
        merged[duplicateIndex] = { ...merged[duplicateIndex], ...message };
      }
      registerIdentity(message, duplicateIndex);
      registerIdentity(merged[duplicateIndex], duplicateIndex);
    }
  );

  const messages = merged
    .map((message, index) => ({ message, index }))
    .sort(compareMessages)
    .map(item => item.message);

  return {
    messages,
    collisionCount,
    dedupedCount,
    keylessCount
  };
};

export const mergeReconnectMessages = (existingMessages, historyMessages) =>
  mergeReconnectMessagesWithStats(existingMessages, historyMessages).messages;

const getMessagesForUser = (state, userId) => {
  const messageMap = state && state.chat && state.chat.messageMap;
  const messages = messageMap && messageMap[userId];
  return Array.isArray(messages) ? messages : [];
};

const isSameMessageSequence = (left, right) =>
  left.length === right.length &&
  left.every((message, index) => message === right[index]);

const getHistoryGeneration = state => {
  const generation = toFiniteNumber(
    state && state.ws && state.ws.historyGeneration
  );
  return generation === undefined ? 0 : generation;
};

const isReconcileStateEligible = state =>
  Boolean(
    state &&
      state.socketConnectSuccess &&
      state.isConversation === true &&
      state.isConversationEnd !== true &&
      !state.isQueue &&
      state.hasHistoryLoad !== true &&
      hasValue(state.userId) &&
      hasValue(state.channelId) &&
      Number(state.channelId) !== 0
  );

const selectCoverageFloor = (
  loadedMessages,
  previousFloorMessages,
  channelId,
  coverageEstablished = false,
  previousMode,
  allowCoverageExpansion = true
) => {
  const loaded = Array.isArray(loadedMessages) ? loadedMessages : [];
  const previous = Array.isArray(previousFloorMessages)
    ? previousFloorMessages
    : [];
  const previousBoundary = createHistoryBoundary(previous, channelId);
  const previousFloor = selectOldestAnchorMessages(previous, previousBoundary);

  const channelMessages = loaded.filter(
    message =>
      message &&
      (!hasValue(channelId) ||
        !hasValue(message.channelId) ||
        String(message.channelId) === String(channelId))
  );
  const positionedMessages = channelMessages.filter(
    message =>
      isCustomerHistoryAnchorCandidate(message) &&
      toStableSort(message && message.sort) !== undefined
  );
  const hasUnsafeUnpositionedMessage = channelMessages.some(
    message =>
      toStableSort(message && message.sort) === undefined &&
      isCustomerHistoryCoverageMessage(message) &&
      !(
        hasValue(message && message.msgId) &&
        message.sendFlag === false &&
        [1, 2, 3].includes(Number(message.SENDSTATUS))
      )
  );
  const hasCompleteSortWindow =
    positionedMessages.length > 0 && !hasUnsafeUnpositionedMessage;
  const loadedBoundary = hasCompleteSortWindow
    ? createHistoryBoundary(positionedMessages, channelId)
    : undefined;
  const loadedFloor = loadedBoundary
    ? selectOldestAnchorMessages(positionedMessages, loadedBoundary)
    : [];
  const resolvedPreviousMode =
    previousMode ||
    (coverageEstablished
      ? previousFloor.length > 0
        ? HISTORY_COVERAGE_MODE.FINITE
        : HISTORY_COVERAGE_MODE.TERMINAL
      : undefined);

  if (
    coverageEstablished &&
    resolvedPreviousMode === HISTORY_COVERAGE_MODE.TERMINAL
  ) {
    return {
      runFloorMessages: [],
      nextFloorMessages: [],
      mode: HISTORY_COVERAGE_MODE.TERMINAL,
      provisionalCandidateFloorMessages: []
    };
  }

  if (
    coverageEstablished &&
    resolvedPreviousMode === HISTORY_COVERAGE_MODE.PROVISIONAL
  ) {
    if (hasUnsafeUnpositionedMessage) {
      return {
        runFloorMessages: [],
        nextFloorMessages: [],
        mode: HISTORY_COVERAGE_MODE.TERMINAL,
        provisionalCandidateFloorMessages: []
      };
    }
    return {
      runFloorMessages: [],
      nextFloorMessages: [],
      mode: HISTORY_COVERAGE_MODE.PROVISIONAL,
      provisionalCandidateFloorMessages: loadedFloor.map(message => ({
        ...message
      }))
    };
  }

  if (coverageEstablished) {
    if (previousFloor.length === 0) {
      return {
        runFloorMessages: [],
        nextFloorMessages: [],
        mode: HISTORY_COVERAGE_MODE.TERMINAL,
        provisionalCandidateFloorMessages: []
      };
    }

    // A server/history record without a stable position makes a bounded floor
    // unsafe. Explicit client optimistic echoes and local UI/command records do
    // not participate in REST coverage and cannot move or poison the floor.
    if (!hasCompleteSortWindow) {
      if (!hasUnsafeUnpositionedMessage && previousFloor.length > 0) {
        return {
          runFloorMessages: previousFloor.map(message => ({ ...message })),
          nextFloorMessages: previousFloor.map(message => ({ ...message })),
          mode: HISTORY_COVERAGE_MODE.FINITE,
          provisionalCandidateFloorMessages: []
        };
      }
      return {
        runFloorMessages: [],
        nextFloorMessages: [],
        mode: HISTORY_COVERAGE_MODE.TERMINAL,
        provisionalCandidateFloorMessages: []
      };
    }

    const nextFloor =
      allowCoverageExpansion &&
      loadedBoundary.minSort < previousBoundary.minSort
        ? loadedFloor
        : previousFloor;

    // The session floor may only stay fixed or move older. It never advances
    // with new WS traffic, so a late-persisted gap remains in the scan range.
    return {
      runFloorMessages: nextFloor.map(message => ({ ...message })),
      nextFloorMessages: nextFloor.map(message => ({ ...message })),
      mode: HISTORY_COVERAGE_MODE.FINITE,
      provisionalCandidateFloorMessages: []
    };
  }

  if (hasUnsafeUnpositionedMessage) {
    return {
      runFloorMessages: [],
      nextFloorMessages: [],
      mode: HISTORY_COVERAGE_MODE.TERMINAL,
      provisionalCandidateFloorMessages: []
    };
  }

  if (!hasCompleteSortWindow) {
    return {
      runFloorMessages: [],
      nextFloorMessages: [],
      mode: HISTORY_COVERAGE_MODE.PROVISIONAL,
      provisionalCandidateFloorMessages: []
    };
  }

  return {
    runFloorMessages: loadedFloor.map(message => ({ ...message })),
    nextFloorMessages: loadedFloor.map(message => ({ ...message })),
    mode: HISTORY_COVERAGE_MODE.FINITE,
    provisionalCandidateFloorMessages: []
  };
};

export const selectHeadCoverageFloor = (
  loadedMessages,
  channelId,
  windowSize = DEFAULT_HEAD_PROBE_WINDOW_MESSAGES
) => {
  const positionedMessages = (Array.isArray(loadedMessages)
    ? loadedMessages
    : []
  ).filter(
    message =>
      message &&
      isCustomerHistoryAnchorCandidate(message) &&
      toStableSort(message.sort) !== undefined &&
      (!hasValue(channelId) ||
        !hasValue(message.channelId) ||
        String(message.channelId) === String(channelId))
  );
  const normalizedWindowSize = Math.max(
    1,
    Number.isFinite(Number(windowSize))
      ? Math.floor(Number(windowSize))
      : DEFAULT_HEAD_PROBE_WINDOW_MESSAGES
  );
  const sorted = positionedMessages
    .slice()
    .sort(
      (left, right) =>
        toStableSort(right && right.sort) - toStableSort(left && left.sort)
    );
  const coverageKeys = new Set();
  let coverageIdentityCount = 0;
  let cutoffSort;
  sorted.some((message, index) => {
    const stableKeys = getStableMessageKeys(message);
    const alreadyCovered = stableKeys.some(key => coverageKeys.has(key));
    if (stableKeys.length > 0) {
      stableKeys.forEach(key => coverageKeys.add(key));
      if (alreadyCovered) return false;
    } else {
      coverageKeys.add(`keyless:${String(index)}`);
    }
    coverageIdentityCount += 1;
    if (coverageIdentityCount < normalizedWindowSize) return false;
    cutoffSort = toStableSort(message.sort);
    return true;
  });
  if (cutoffSort === undefined && sorted.length > 0) {
    cutoffSort = toStableSort(sorted[sorted.length - 1].sort);
  }
  const recentMessages = sorted.filter(
    message => toStableSort(message && message.sort) >= cutoffSort
  );
  const boundary = createHistoryBoundary(recentMessages, channelId);
  return selectOldestAnchorMessages(recentMessages, boundary).map(message => ({
    ...message
  }));
};

const countGapCandidates = (messages, anchorMessages, boundary) => {
  const identities = new Set();
  const anchorMatcher = createStableAnchorMatcher(anchorMessages);

  (Array.isArray(messages) ? messages : []).forEach(message => {
    const sort = toStableSort(message && message.sort);
    if (
      sort === undefined ||
      boundary.maxSort === undefined ||
      sort > boundary.maxSort
    ) {
      return;
    }

    if (anchorMatcher.matches(message)) return;
    const identity = getMessageIdentity(message);
    if (identity.msgIdKey || identity.sortKey) {
      identities.add(identity.msgIdKey || identity.sortKey);
    }
  });

  return identities.size;
};

/**
 * Execute one atomic reconciliation against a fixed pre-request snapshot.
 * Scheduling and trigger coalescing are intentionally owned by the manager.
 */
export const reconcileHistoryOnce = async ({
  state,
  commit,
  getHistoryMessage,
  generation = getHistoryGeneration(state),
  anchorMessages,
  coverageMode,
  allowCoverageExpansion = true,
  lockCoverageFloor = false,
  continuation,
  maxPages = DEFAULT_MAX_PAGES,
  maxDurationMs = DEFAULT_MAX_CHUNK_DURATION_MS,
  provisionalWindowMessages = DEFAULT_HEAD_PROBE_WINDOW_MESSAGES,
  now = () => Date.now(),
  requestOptions,
  retryDelays = DEFAULT_RETRY_DELAYS,
  waitForRetry = wait,
  onCoverageEstablished
}) => {
  if (typeof getHistoryMessage !== "function") {
    throw new TypeError("getHistoryMessage must be a function");
  }

  if (!isReconcileStateEligible(state)) {
    return { status: "skipped", addedCount: 0, pageCount: 0 };
  }

  const userId = state.userId;
  const channelId = state.channelId;
  const sessionToken = state.token;
  const loadedMessages = getMessagesForUser(state, userId).filter(
    message =>
      message &&
      (!hasValue(message.channelId) ||
        String(message.channelId) === String(channelId))
  );
  const previousFloorMessages =
    continuation && Array.isArray(continuation.anchorMessages)
      ? continuation.anchorMessages
      : anchorMessages;
  const previousCoverageMode =
    (continuation && continuation.coverageMode) || coverageMode;
  const coverageEstablished = Boolean(
    (continuation &&
      Object.prototype.hasOwnProperty.call(continuation, "anchorMessages")) ||
      Array.isArray(anchorMessages)
  );
  const coverage = lockCoverageFloor
    ? {
        runFloorMessages: Array.isArray(anchorMessages)
          ? anchorMessages.map(message => ({ ...message }))
          : [],
        nextFloorMessages: Array.isArray(anchorMessages)
          ? anchorMessages.map(message => ({ ...message }))
          : [],
        mode:
          Array.isArray(anchorMessages) && anchorMessages.length > 0
            ? HISTORY_COVERAGE_MODE.FINITE
            : HISTORY_COVERAGE_MODE.TERMINAL,
        provisionalCandidateFloorMessages: []
      }
    : selectCoverageFloor(
        loadedMessages,
        previousFloorMessages,
        channelId,
        coverageEstablished,
        previousCoverageMode,
        allowCoverageExpansion
      );
  const snapshot = loadedMessages.map(message => ({ ...message }));
  const runFloorMessages = coverage.runFloorMessages;
  const snapshotBoundary = createHistoryBoundary(snapshot, channelId);

  // The first floor belongs to the session scope, not to one request result.
  // Publish it before I/O so a failed or yielded first scan cannot later move
  // the floor toward newer messages.
  if (typeof onCoverageEstablished === "function") {
    onCoverageEstablished(
      coverage.nextFloorMessages.map(message => ({ ...message })),
      coverage.mode
    );
  }

  const scopeIsCurrent = () =>
    isReconcileStateEligible(state) &&
    getHistoryGeneration(state) === generation &&
    String(state.userId) === String(userId) &&
    String(state.channelId) === String(channelId) &&
    String(state.token) === String(sessionToken);

  const history = await collectReconnectHistory({
    existingMessages: snapshot,
    coverageFloorMessages: runFloorMessages,
    channelId,
    retryDelays,
    waitForRetry,
    continuation: continuation && continuation.collection,
    maxPages,
    maxDurationMs,
    now,
    shouldContinue: scopeIsCurrent,
    fetchPage: beginTime =>
      getHistoryMessage(
        {
          userId,
          beginTime,
          messageType: 0
        },
        requestOptions
      )
  });

  if (history.cancelled || !scopeIsCurrent()) {
    return {
      status: "stale",
      addedCount: 0,
      pageCount: history.pageCount,
      malformedCount: history.malformedCount,
      staleDiscardCount: 1
    };
  }

  const displayableHistoryMessages = history.messages.filter(
    isCustomerVisibleMessage
  );
  const currentMessages = getMessagesForUser(state, userId).slice();
  const mergeResult = mergeReconnectMessagesWithStats(
    currentMessages,
    displayableHistoryMessages
  );
  const addedCount = Math.max(
    0,
    mergeResult.messages.length - currentMessages.length
  );
  let nextCoverageMode = coverage.mode;
  let provisionalCandidateFloorMessages =
    coverage.provisionalCandidateFloorMessages || [];
  if (
    !history.continuation &&
    coverage.mode === HISTORY_COVERAGE_MODE.PROVISIONAL
  ) {
    const postMergeCoverage = selectCoverageFloor(
      mergeResult.messages,
      [],
      channelId,
      false
    );
    if (postMergeCoverage.mode === HISTORY_COVERAGE_MODE.TERMINAL) {
      nextCoverageMode = HISTORY_COVERAGE_MODE.TERMINAL;
      provisionalCandidateFloorMessages = [];
    } else {
      provisionalCandidateFloorMessages =
        postMergeCoverage.mode === HISTORY_COVERAGE_MODE.FINITE
          ? selectHeadCoverageFloor(
              mergeResult.messages,
              channelId,
              provisionalWindowMessages
            )
          : [];
    }
  }

  if (!scopeIsCurrent()) {
    return {
      status: "stale",
      addedCount: 0,
      pageCount: history.pageCount,
      malformedCount: history.malformedCount,
      staleDiscardCount: 1
    };
  }

  if (!isSameMessageSequence(currentMessages, mergeResult.messages)) {
    commit(
      "chat/setMessages",
      {
        customerId: userId,
        messages: mergeResult.messages,
        historyReconcileScope: { channelId, generation }
      },
      { root: true }
    );
  }

  return {
    status: history.continuation ? "partial" : "synced",
    addedCount,
    pageCount: history.pageCount,
    candidateCount: displayableHistoryMessages.length,
    dedupedCount: mergeResult.dedupedCount,
    collisionCount: mergeResult.collisionCount,
    keylessCount: mergeResult.keylessCount,
    malformedCount: history.malformedCount,
    gapCount: countGapCandidates(
      displayableHistoryMessages,
      snapshot,
      snapshotBoundary
    ),
    usedAnchorMessages: runFloorMessages,
    coverageFloorMessages: coverage.nextFloorMessages,
    coverageMode: nextCoverageMode,
    provisionalCandidateFloorMessages: provisionalCandidateFloorMessages.map(
      message => ({ ...message })
    ),
    continuation: history.continuation
      ? {
          anchorMessages: runFloorMessages.map(message => ({ ...message })),
          coverageMode: coverage.mode,
          collection: history.continuation
        }
      : undefined,
    staleDiscardCount: 0
  };
};
